import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { DataTable } from 'components/data-table';
import { permitsColumns, type PermitRow } from './permits-table-columns';
import type { PermitsResult } from 'types/api-responses';
import style from './permits-table.module.scss';

interface PermitsTableProps {
  resourceHref: string;
  uwi: string;
  /**
   * Resolves the rows as-of a workspace: a promoted one reads as of its `promoted_at`,
   * a draft reads live with its staged edits overlaid. Omitted on the Well page, which
   * always reads live.
   *
   * Sent as `workspace_id` INSIDE `query`, which is the only shape this endpoint reads:
   * it permits off `params.accept(:query)` and, unlike the wells controller, does not
   * underscore incoming keys. camelCase, or the same key at the top level, is dropped by
   * strong params without an error.
   */
  workspaceId?: number;
}

export const PermitsTable = ({
  uwi,
  resourceHref,
  workspaceId,
}: PermitsTableProps) => {
  const { searchAtom } = useMemo(
    () =>
      searchAtomPair<
        PermitsResult,
        SearchParams & {
          query: { uwi10: string; workspace_id?: number };
        }
      >(
        { href: resourceHref },
        uwi
          ? {
              query: {
                uwi10: uwi,
                ...(workspaceId ? { workspace_id: workspaceId } : {}),
              },
            }
          : undefined
      ),
    [resourceHref, uwi, workspaceId]
  );

  const { loading, data, error } = useAtomValue(searchAtom);

  return (
    <div className={style.permitsDetails}>
      <DataTable<PermitRow>
        data={(data?.results ?? []) as PermitRow[]}
        columns={permitsColumns}
        getRowId={(row) => String(row.id)}
        isLoading={loading ?? true}
        error={error ?? null}
        searchPlaceholder="Search permits…"
        config={{
          sorting: true,
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          columnVisibility: true,
          globalSearch: true,
          columnFilters: true,
          export: {
            exportPresets: [
              {
                name: 'Export',
                format: 'xlsx',
                filename: 'permits-export',
              },
            ],
          },
        }}
        initialTableState={{
          sortColumn: 'originalPermitLicenseDate',
          sortDirection: 'desc',
        }}
      />
    </div>
  );
};
